/**
 * Single source of nav item config.
 * Shared by Sidebar (desktop) and TabBar (mobile).
 * Icons are React functional components returning inline SVG —
 * no dangerouslySetInnerHTML, no XSS surface.
 */
import type { FC, SVGProps } from "react";

export type NavItem = {
  key: string;
  /** i18n key under chrome.nav.* */
  labelKey: string;
  route: string;
  Icon: FC<SVGProps<SVGSVGElement>>;
};

// ── Icon components ───────────────────────────────────────────────────────────
// All share the same base attributes; callers may override width/height.

function IconBase(
  props: SVGProps<SVGSVGElement> & { children: React.ReactNode },
) {
  const { children, ...rest } = props;
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const IconVandaag: FC<SVGProps<SVGSVGElement>> = (props) => (
  <IconBase {...props}>
    <path d="M3 11l9-8 9 8M5 9.5V20h14V9.5" />
  </IconBase>
);

export const IconOefenen: FC<SVGProps<SVGSVGElement>> = (props) => (
  <IconBase {...props}>
    <path d="M12 4v16M4 8l8-4 8 4" />
    <path d="M5 10v8a2 2 0 002 2h10a2 2 0 002-2v-8" />
  </IconBase>
);

export const IconWoorden: FC<SVGProps<SVGSVGElement>> = (props) => (
  <IconBase {...props}>
    <path d="M4 5a2 2 0 012-2h9l5 5v11a2 2 0 01-2 2H6a2 2 0 01-2-2z" />
    <path d="M9 9h6M9 13h6" />
  </IconBase>
);

export const IconGrammatica: FC<SVGProps<SVGSVGElement>> = (props) => (
  <IconBase {...props}>
    <path d="M4 19V5a2 2 0 012-2h13" />
    <path d="M4 19a2 2 0 002 2h13a2 2 0 002-2V9l-5-5" />
    <path d="M9 12h6M9 15h4" />
  </IconBase>
);

export const IconVoortgang: FC<SVGProps<SVGSVGElement>> = (props) => (
  <IconBase {...props}>
    <path d="M4 19V5M4 19h16M8 16l4-5 3 3 4-6" />
  </IconBase>
);

export const IconSettings: FC<SVGProps<SVGSVGElement>> = (props) => (
  <IconBase {...props}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19 12a7 7 0 00-.1-1.2l2-1.6-2-3.4-2.4 1a7 7 0 00-2-1.2L14 1h-4l-.5 2.6a7 7 0 00-2 1.2l-2.4-1-2 3.4 2 1.6A7 7 0 005 12c0 .4 0 .8.1 1.2l-2 1.6 2 3.4 2.4-1a7 7 0 002 1.2L10 23h4l.5-2.6a7 7 0 002-1.2l2.4 1 2-3.4-2-1.6c.1-.4.1-.8.1-1.2z" />
  </IconBase>
);

// ── Nav item definitions ──────────────────────────────────────────────────────

export const PRIMARY_NAV_ITEMS: NavItem[] = [
  {
    key: "vandaag",
    labelKey: "chrome.nav.vandaag",
    route: "/vandaag",
    Icon: IconVandaag,
  },
  {
    key: "oefenen",
    labelKey: "chrome.nav.oefenen",
    route: "/oefenen",
    Icon: IconOefenen,
  },
  {
    key: "woorden",
    labelKey: "chrome.nav.woorden",
    route: "/woorden",
    Icon: IconWoorden,
  },
  {
    key: "grammatica",
    labelKey: "chrome.nav.grammatica",
    route: "/grammatica",
    Icon: IconGrammatica,
  },
  {
    key: "voortgang",
    labelKey: "chrome.nav.voortgang",
    route: "/voortgang",
    Icon: IconVoortgang,
  },
];

/** Tab bar shows 4 items (Grammatica reachable from MobileTopBar instead) */
export const TAB_BAR_ITEMS = PRIMARY_NAV_ITEMS.filter(
  (item) => item.key !== "grammatica",
);
